
export const BALL_VERTEX_SHADER = `#version 300 es
layout(location=0) in vec2 a_position;

// Instance attributes
layout(location=1) in vec2 i_pos;
layout(location=2) in float i_radius;
layout(location=3) in vec3 i_color;
layout(location=4) in vec2 i_vel;
layout(location=5) in float i_angle;
layout(location=6) in float i_alpha;

uniform vec2 u_resolution;
uniform vec2 u_translate;
uniform float u_scale;
uniform float u_time;
uniform float u_pulseSpeed;
uniform float u_squashFactor;
uniform float i_borderWidth; // Global for now
uniform int u_ballStyle; // 0=SHINY, 1=NEON, 2=CHROME, 3=MATTE, 4=GHOST, 5=CRYSTAL, 6=METAL, 7=GLOW, 8=WIREFRAME, 9=HOLOGRAPHIC, 10=PLASMA, 11=FROSTED
uniform int u_textureType; // 0=NONE, 1=STRIPES, 2=DOTS, 3=CROSSHATCH, 4=NOISE, 5=CHECKERBOARD, 6=HONEYCOMB, 7=CIRCUIT, 8=WAVES, 9=STARS, 10=BRICKS, 11=SWIRL
uniform float u_textureOpacity;
uniform float u_glowAmount;

out vec2 v_uv;
out vec3 v_color;
out float v_radius;
out float v_alpha;
out float v_borderWidth;
flat out int v_ballStyle;
flat out int v_textureType;
out float v_textureOpacity;
out float v_glowAmount;

void main() {
    v_uv = a_position;
    v_color = i_color;
    v_alpha = i_alpha;
    v_borderWidth = i_borderWidth;
    v_ballStyle = u_ballStyle;
    v_textureType = u_textureType;
    v_textureOpacity = u_textureOpacity;
    v_glowAmount = u_glowAmount;
    
    // Pulse Effect
    float pulse = 1.0;
    if (u_pulseSpeed > 0.0) {
        pulse = 1.0 + sin(u_time * 0.005 * u_pulseSpeed) * 0.1;
    }
    float r = i_radius * pulse;
    v_radius = r;

    // Expand quad for glow
    float expand = 1.0 + (u_glowAmount / max(r, 1.0));
    v_uv = a_position * expand;

    // Base vertex position (scaled by radius and expansion)
    vec2 vertexPos = a_position * r * expand;
    
    // Squash & Stretch
    if (u_squashFactor > 0.0) {
        float velMag = length(i_vel);
        if (velMag > 0.5) {
            float angle = atan(i_vel.y, i_vel.x);
            float maxStretch = 1.0 + (u_squashFactor * 0.5);
            float stretch = min(maxStretch, 1.0 + velMag * 0.02 * u_squashFactor);
            float squash = 1.0 / sqrt(stretch);
            
            // Rotate to velocity direction
            float c = cos(angle);
            float s = sin(angle);
            mat2 rot = mat2(c, s, -s, c);
            mat2 rotInv = mat2(c, -s, s, c);
            
            // Apply scale
            vertexPos = rotInv * (vec2(stretch, squash) * (rot * vertexPos));
        }
    }
    
    // Translate (Instance)
    vec2 worldPos = vertexPos + i_pos;

    // Camera Transform
    vec2 camPos = (worldPos * u_scale) + u_translate;
    
    // Screen space to Clip space
    vec2 clipPos = (camPos / u_resolution) * 2.0 - 1.0;
    clipPos.y *= -1.0; 

    gl_Position = vec4(clipPos, 0.0, 1.0);
}
`;

export const BALL_FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 v_uv;
in vec3 v_color;
in float v_radius;
in float v_alpha;
in float v_borderWidth;
flat in int v_ballStyle;
flat in int v_textureType;
in float v_textureOpacity;
in float v_glowAmount;

uniform vec3 u_borderColor;
uniform float u_strokeOpacity;
uniform float u_glowOpacity;
uniform float u_fillOpacity;
uniform vec3 u_glowColor;

out vec4 fragColor;

void main() {
    float dist = length(v_uv);
    
    // Anti-aliasing
    // Using a fixed 1.5 pixel width for anti-aliasing is smoother and more consistent than fwidth
    float delta = 1.5 / v_radius;
    float ballAlpha = (1.0 - smoothstep(1.0 - delta, 1.0, dist)) * u_fillOpacity;

    // Glow effect (outside the circle)
    float glowAlpha = 0.0;
    if (v_glowAmount > 0.0) {
        // Glow falls off exponentially outside the ball
        glowAlpha = exp(-4.0 * (dist - 1.0) * (100.0 / v_glowAmount)) * step(1.0, dist) * u_glowOpacity;
    }

    vec3 baseColor = v_color;
    
    // --- Texture Logic ---
    float textureVal = 0.0;
    vec2 texCoord = v_uv * 5.0; // Scale for pattern density
    
    if (v_textureType == 1) { // STRIPES
        textureVal = step(0.5, fract(texCoord.x));
    } else if (v_textureType == 2) { // DOTS
        textureVal = step(0.7, length(fract(texCoord) - 0.5));
    } else if (v_textureType == 3) { // CROSSHATCH
        textureVal = max(step(0.9, fract(texCoord.x + texCoord.y)), step(0.9, fract(texCoord.x - texCoord.y)));
    } else if (v_textureType == 4) { // NOISE
        textureVal = fract(sin(dot(v_uv, vec2(12.9898, 78.233))) * 43758.5453);
    } else if (v_textureType == 5) { // CHECKERBOARD
        vec2 check = floor(texCoord);
        textureVal = mod(check.x + check.y, 2.0);
    } else if (v_textureType == 6) { // HONEYCOMB
        vec2 q = texCoord;
        q.x *= 0.866;
        q.y += mod(floor(q.x), 2.0) * 0.5;
        vec2 f = fract(q) - 0.5;
        textureVal = step(0.4, length(f));
    } else if (v_textureType == 7) { // CIRCUIT
        vec2 grid = fract(texCoord * 2.0);
        textureVal = step(0.95, grid.x) + step(0.95, grid.y);
        if (length(grid - 0.5) < 0.2) textureVal = 1.0;
    } else if (v_textureType == 8) { // WAVES
        textureVal = step(0.5, sin(texCoord.x * 10.0 + texCoord.y * 5.0));
    } else if (v_textureType == 9) { // STARS
        vec2 f = fract(texCoord) - 0.5;
        float star = 1.0 - smoothstep(0.0, 0.2, length(f) * (1.0 + 0.8 * sin(atan(f.y, f.x) * 5.0)));
        textureVal = star;
    } else if (v_textureType == 10) { // BRICKS
        vec2 b = texCoord * 2.0;
        b.x += step(1.0, mod(b.y, 2.0)) * 0.5;
        vec2 fb = fract(b);
        textureVal = step(0.9, fb.x) + step(0.9, fb.y);
    } else if (v_textureType == 11) { // SWIRL
        float r = length(v_uv);
        float a = atan(v_uv.y, v_uv.x);
        textureVal = step(0.5, sin(a * 5.0 + r * 10.0));
    }

    if (v_textureType > 0) {
        baseColor = mix(baseColor, vec3(1.0), textureVal * v_textureOpacity);
    }

    if (v_ballStyle == 0) { // SHINY
        float highlight = smoothstep(0.4, 0.0, length(v_uv - vec2(-0.3, -0.3)));
        baseColor += highlight * 0.4;
    } else if (v_ballStyle == 1) { // NEON
        float neonGlow = exp(-3.0 * dist);
        baseColor = mix(baseColor, vec3(1.0), neonGlow * 0.5);
        baseColor += v_color * (1.0 - dist) * 0.3;
    } else if (v_ballStyle == 2) { // CHROME
        vec2 reflectDir = normalize(v_uv + vec2(0.5, 0.5));
        float reflection = smoothstep(0.4, 0.5, reflectDir.x * reflectDir.y);
        baseColor = mix(baseColor, vec3(1.0), reflection * 0.6);
        float spec = pow(1.0 - dist, 2.0);
        baseColor = mix(baseColor, vec3(1.0), spec * 0.7);
    } else if (v_ballStyle == 4) { // GHOST
        // Ghost is handled by alpha
    } else if (v_ballStyle == 5) { // CRYSTAL
        float facet = sin(v_uv.x * 10.0) * sin(v_uv.y * 10.0);
        baseColor += facet * 0.2;
        baseColor = mix(baseColor, vec3(1.0), pow(dist, 4.0) * 0.5);
    } else if (v_ballStyle == 6) { // METAL
        float sheen = abs(v_uv.x + v_uv.y);
        baseColor = mix(baseColor, vec3(0.8, 0.8, 0.9), step(0.9, sheen) * 0.2);
        baseColor *= (0.8 + 0.4 * dist);
    } else if (v_ballStyle == 7) { // GLOW
        float innerGlow = pow(dist, 2.0);
        baseColor *= innerGlow;
        baseColor += v_color * (1.0 - dist) * 0.5;
    } else if (v_ballStyle == 8) { // WIREFRAME
        float grid = step(0.9, fract(v_uv.x * 10.0)) + step(0.9, fract(v_uv.y * 10.0));
        if (dist > 0.98) grid = 1.0;
        baseColor = v_color * grid;
        ballAlpha *= grid;
    } else if (v_ballStyle == 9) { // HOLOGRAPHIC
        float scanline = sin(v_uv.y * 50.0) * 0.1;
        baseColor += vec3(scanline);
        baseColor.r += sin(dist * 10.0) * 0.2;
        baseColor.g += sin(dist * 10.0 + 2.0) * 0.2;
        baseColor.b += sin(dist * 10.0 + 4.0) * 0.2;
    } else if (v_ballStyle == 10) { // PLASMA
        float p = sin(v_uv.x * 5.0 + u_time * 0.005) * cos(v_uv.y * 5.0 - u_time * 0.005);
        baseColor = mix(baseColor, vec3(1.0, 0.5, 0.0), p * 0.5 + 0.5);
    } else if (v_ballStyle == 11) { // FROSTED
        float grain = fract(sin(dot(v_uv, vec2(12.9898, 78.233))) * 43758.5453);
        baseColor = mix(baseColor, vec3(1.0), grain * 0.2);
        ballAlpha *= 0.8;
    }

    // Apply border INSIDE the ball
    if (v_borderWidth > 0.0) {
        float borderStart = 1.0 - (v_borderWidth / v_radius);
        float borderMask = smoothstep(borderStart - delta, borderStart + delta, dist) * ballAlpha;
        baseColor = mix(baseColor, u_borderColor, borderMask * u_strokeOpacity);
    }

    // Final color and alpha blending
    // If inside ball, use baseColor (with border). If outside, use glow.
    vec3 finalColor = mix(u_glowColor, baseColor, ballAlpha);
    float finalAlpha = max(ballAlpha, glowAlpha) * v_alpha;
    
    if (v_ballStyle == 4) { // GHOST
        finalAlpha *= 0.5;
    }

    fragColor = vec4(finalColor, finalAlpha);
}
`;

export const RING_VERTEX_SHADER_FINAL = `#version 300 es
layout(location=0) in vec2 a_position;

layout(location=1) in float i_radius;
layout(location=2) in float i_angle;
layout(location=3) in vec3 i_color;
layout(location=4) in float i_alpha;
layout(location=5) in float i_gapSize;

uniform vec2 u_resolution;
uniform vec2 u_translate;
uniform float u_scale;
uniform float u_thickness;
uniform vec2 u_center; // Center of the container

out vec2 v_pos; // Local position in pixels relative to center
out vec3 v_color;
out float v_radius;
out float v_angle;
out float v_alpha;
out float v_gapSize;

void main() {
    float size = i_radius + u_thickness + 5.0; // Padding
    vec2 localPos = a_position * size;
    v_pos = localPos;
    v_color = i_color;
    v_radius = i_radius;
    v_angle = i_angle;
    v_alpha = i_alpha;
    v_gapSize = i_gapSize;

    vec2 worldPos = localPos + u_center;
    vec2 camPos = (worldPos * u_scale) + u_translate;
    
    vec2 clipPos = (camPos / u_resolution) * 2.0 - 1.0;
    clipPos.y *= -1.0;

    gl_Position = vec4(clipPos, 0.0, 1.0);
}
`;

export const RING_FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 v_pos;
in vec3 v_color;
in float v_radius;
in float v_angle;
in float v_alpha;
in float v_gapSize;

uniform float u_thickness;
uniform int u_shape;     // 0=Circle, 3=Tri, 4=Square, etc.
uniform int u_nextShape;
uniform float u_morphFactor;
uniform float u_containerRotation; // Global rotation

out vec4 fragColor;

#define PI 3.14159265359
#define TWO_PI 6.28318530718

// SDF Functions
float getRadiusAtAngle(float angle, int n) {
    if (n == 0) return 1.0;
    float a = angle;
    float n_float = float(n);
    return cos(PI / n_float) / cos(mod(a, TWO_PI / n_float) - PI / n_float);
}

float sdShape(vec2 p, int shape, float r) {
    if (shape == 0) return length(p) - r;
    
    float angle = atan(p.y, p.x) - u_containerRotation;
    float shapeR = r * getRadiusAtAngle(angle, shape);
    
    return length(p) - shapeR;
}

void main() {
    // 1. Calculate Distance to Shape
    float d1 = sdShape(v_pos, u_shape, v_radius);
    float d2 = sdShape(v_pos, u_nextShape, v_radius);
    float dist = mix(d1, d2, u_morphFactor);
    
    // 2. Ring Mask
    float halfThick = u_thickness * 0.5;
    float alpha = 1.0 - smoothstep(halfThick - 0.75, halfThick + 0.75, abs(dist));
    
    if (alpha < 0.01) discard;
    
    // 3. Gap Logic
    float currentAngle = atan(v_pos.y, v_pos.x);
    float diff = abs(mod(currentAngle - v_angle + PI, TWO_PI) - PI);
    
    if (diff < v_gapSize * 0.5) {
        discard;
    }
    
    fragColor = vec4(v_color, alpha * v_alpha);
}
`;

export const BLAST_VERTEX_SHADER = `#version 300 es
layout(location=0) in vec2 a_position;

layout(location=1) in vec2 i_pos;
layout(location=2) in float i_radius;
layout(location=3) in vec3 i_color;
layout(location=4) in float i_alpha;

uniform vec2 u_resolution;
uniform vec2 u_translate;
uniform float u_scale;

out vec2 v_uv;
out vec3 v_color;
out float v_alpha;
out float v_radius;

void main() {
    v_uv = a_position;
    v_color = i_color;
    v_alpha = i_alpha;
    v_radius = i_radius;

    vec2 worldPos = (a_position * i_radius) + i_pos;
    vec2 camPos = (worldPos * u_scale) + u_translate;
    
    vec2 clipPos = (camPos / u_resolution) * 2.0 - 1.0;
    clipPos.y *= -1.0;

    gl_Position = vec4(clipPos, 0.0, 1.0);
}
`;

export const BLAST_FRAGMENT_SHADER = `#version 300 es
precision highp float;

in vec2 v_uv;
in vec3 v_color;
in float v_alpha;
in float v_radius;

uniform float u_thickness;

out vec4 fragColor;

void main() {
    float dist = length(v_uv);
    float normThick = u_thickness / v_radius;
    float halfThick = normThick * 0.5;
    
    float ringDist = abs(dist - (1.0 - halfThick));
    float delta = 1.5 / v_radius;
    float alpha = 1.0 - smoothstep(halfThick - delta, halfThick + delta, ringDist);
    
    fragColor = vec4(v_color, alpha * v_alpha);
}
`;
